import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, signal, viewChild, OnInit } from "@angular/core";
import { FormsModule, NgForm } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { ActivatedRoute, Router } from "@angular/router";
import { MatButtonLoadingDirective } from "app/common-ux/button/button-loading.directive";
import { ValuesEqualValidatorDirective } from "app/common-ux/values-equal-validator.directive";
import { AuthenticationService } from "app/auth/authentication.service";
import { DialogService } from "app/common-ux/dialog/dialog.service";
import { TenantService } from "app/tenant/module/tenant.service";

@Component({
    selector: "app-reset-password",
    templateUrl: "./reset-password.component.html",
    styleUrls: ["./reset-password.component.scss"],
    imports: [
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonLoadingDirective,
        ValuesEqualValidatorDirective,
    ],
})
export class ResetPasswordComponent implements OnInit {
    private authenticationService = inject(AuthenticationService);
    private dialogService = inject(DialogService);
    private tenantService = inject(TenantService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);

    public resetForm = viewChild.required<NgForm>("resetForm");
    public model = {
        newPassword: "",
        confirmPassword: "",
    };
    public resetState = signal({
        isInvalid: false,
        invalidReason: "",
        isResetting: false,
        resetSuccessfully: false,
    });

    public isInvalidLink = signal(false);
    public resetSending = signal(false);
    public resetSent = signal(false);

    public ngOnInit() {
        const expiryParam = this.route.snapshot.queryParams.expiry;
        if (!expiryParam) {
            return;
        }

        const expiry = new Date(expiryParam);
        this.isInvalidLink.set(expiry < new Date());
    }

    public async resetPassword() {
        try {
            this.resetState.update((s) => ({ ...s, isInvalid: false, isResetting: true }));
            const email = this.route.snapshot.queryParams.email;
            const token = this.route.snapshot.queryParams.token;
            await this.authenticationService.resetPassword(email, token, this.model.newPassword);
            this.resetForm().form.disable();
            this.resetState.update((s) => ({ ...s, resetSuccessfully: true }));

            const user = await this.authenticationService.login(email, this.model.newPassword);
            const redirect = this.route.snapshot.queryParams.redirect;
            if (redirect) {
                await this.router.navigateByUrl(redirect);
            } else {
                const url = this.tenantService.getDefaultUrlForUser(user);
                await this.router.navigateByUrl(url);
            }

            this.dialogService.success("Presto!");
        } catch (e) {
            const response = e as HttpErrorResponse;
            if (response.status === 400) {
                this.isInvalidLink.set(true);
            } else {
                this.resetState.update((s) => ({
                    ...s,
                    isInvalid: true,
                    invalidReason: "Unknown error when resetting password",
                }));
            }
        } finally {
            this.resetState.update((s) => ({ ...s, isResetting: false }));
        }
    }

    public async sendResetLink() {
        try {
            this.resetSending.set(true);
            const email = this.route.snapshot.queryParams.email;
            const redirect = this.route.snapshot.queryParams.redirect;
            await this.authenticationService.requestPasswordReset(email, redirect);
            this.resetSent.set(true);
        } finally {
            this.resetSending.set(false);
        }
    }
}
